import { SCHEMA } from '@vedalib/editor/lib/brand'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import React, { useCallback, useMemo } from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import {
  FieldColorPicker,
  FieldFileId,
  FieldNumber,
  FieldSegmented,
  FieldSlider,
  FormField,
} from 'components/formKit'
import { TileButton } from 'components/uiKit/Button'
import { Typography } from 'components/uiKit/Typography'
import { BrandFieldShadow, BrandTagEdited } from 'services/Branding/components'
import { BORDER_OPTIONS } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { isAnyEdited } from 'services/Branding/fieldConfigs/utils'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'

import type { LabeledGraphicFormBrandType } from './labeledGraphicTypes'

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.labeledGraphic.style, {
  image: {
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  point: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.labeledGraphic.schema.imageBackground'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false },
    }),
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    height: genBrandField({
      as: FieldNumber,
      label: t('input.label.height'),
      rules: [{ min: 1, message: ' ' }],
      props: { width: 64 },
    }),
    width: genBrandField({
      as: FieldNumber,
      label: t('input.label.width'),
      rules: [{ min: 1, message: ' ' }],
      props: { width: 64 },
    }),
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.labeledGraphic.schema.hover'),
      useConfigs: [useBrandColorOptions],
    }),
  },
  hover: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.hoverColor'),
      props: { transparent: true, labeled: true, resettable: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  tooltip: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  navigation: {
    switch: genBrandField({
      as: FieldSegmented<boolean>,
      label: t('elements.labeledGraphic.schema.switcher'),
      props: { fluid: false },
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.labeledGraphic.schema.boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
  animation: {
    fade: genBrandField({
      as: FieldSegmented<boolean>,
      label: t('elements.labeledGraphic.schema.smooth'),
      props: { fluid: false },
    }),
    pulse: genBrandField({
      as: FieldSegmented<boolean>,
      label: t('elements.labeledGraphic.schema.pulse'),
      props: { fluid: false },
    }),
  },
})

const LabeledGraphicFormStyle: LabeledGraphicFormBrandType = ({
  device,
  subConfigs,
  edited,
  setForm,
}) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom bottomSpace gap sides>
        <Typography weight='medium'>{t('elements.labeledGraphic.tags.image')}</Typography>
        <FormField config={schema.image.borderRadius} />
        <FormField config={schema.image.borderStyle} />
        <FormField config={schema.image.borderColor} />
        <FormField config={schema.image.borderWidth} />

        <Typography weight='medium'>{t('elements.labeledGraphic.tags.point')}</Typography>
        <FormField config={schema.point.backgroundImage} />
        <FormField config={schema.point.backgroundColor} />
        <FormField config={schema.point.height} />
        <FormField config={schema.point.width} />
        <FormField config={schema.point.color} />
        <FormField config={schema.hover.backgroundColor} />

        <Typography weight='medium'>{t('elements.labeledGraphic.tags.tooltip')}</Typography>
        <FormField config={schema.tooltip.backgroundColor} />
        <FormField config={schema.navigation.switch} />

        <Typography weight='medium'>{t('style.effects')}</Typography>
        <FormField config={schema.effects.boxShadow} />

        <Typography weight='medium'>{t('elements.labeledGraphic.tags.animation')}</Typography>
        <FormField config={schema.animation.fade} />
        <FormField config={schema.animation.pulse} />

        <TileButton
          description={t('uiKit.button.richTextDescription')}
          icon='otherRight'
          name='showFontForm'
          onClick={handleSetTypography}
        >
          <Typography weight='medium'>{t('page.brand.tab.typography')}</Typography>
          {isAnyEdited(edited.font) && <BrandTagEdited />}
        </TileButton>
      </Padding>
    </LayoutScroll>
  )
}

export default React.memo(LabeledGraphicFormStyle) as typeof LabeledGraphicFormStyle
